import { capitalize, union } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { DEFAULT_SORT_BY, STATIC_COLUMNS } from '~/common/constants/search';
import { EditCharts } from '~/components/DashboardV2/EditCharts/EditCharts';
import SaveDashboard from '~/components/DashboardV2/SaveDashboard/SaveDashboard';
import BaseLoadView from '~/components/Shared/BaseLoadView/BaseLoadView';
import BaseSaveView from '~/components/Shared/BaseSaveView/BaseSaveView';
import { Charts } from '~/components/Shared/Charts/Charts';
import { Box } from '~/eds/src/components/Box/Box';
import { ContentContainer } from '~/eds/src/components/ContentContainer/ContentContainer';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Text } from '~/eds/src/components/Text/Text';
import { TableViewContextType } from '~/enums/TableViewType';
import { Filter } from '~/evifields/src/types/Filter';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { useDocumentDetails } from '~/hooks/useDocumentDetails';
import { usePinnedFilters } from '~/hooks/usePinnedFilters';
import { useSearchNavigation } from '~/hooks/useSearchNavigation';
import { useTableState } from '~/hooks/useTableState';
import { useTrackSegment } from '~/hooks/useTrackSegment ';
import { api } from '~/redux/api/createApi';
import { Dashboard as DashboardType } from '~/redux/api/methods/dashboardV2';
import { SearchItem } from '~/redux/api/methods/evisearch';
import { slices } from '~/redux/slices';
import {
  MODAL_DOCUMENTS_COLUMN_FIELDS_SAVE,
  MODAL_DOCUMENTS_COLUMN_FIELDS_VIEW_SWITCH,
} from '~/types/modal.types';
import { Nullable } from '~/types/Nullable';
import { testIsAdmin } from '~/utils/user';

import {
  getDocumentHandlers,
  testIsQueryBuilderSupported,
} from '../advanced-search/AdvancedSearch.utils';
import { BooleanSearchBar } from '../advanced-search/boolean-search-bar/BooleanSearchBar';
import { CrossFilters } from '../advanced-search/cross-filters/CrossFilters';
import { Filters } from '../advanced-search/filters/Filters';
import { attachRenderersAndSerializers } from '../advanced-search/filters/Filters.utils';
import { QueryBuilder } from '../advanced-search/query-builder/QueryBuilder';
import { ReadOnlyComplexQuery } from '../advanced-search/read-only-complex-query/ReadOnlyComplexQuery';
import { ResultsTable } from '../advanced-search/results-table/ResultsTable';
import { SearchQuery } from '../advanced-search/types';
import {
  buildComplexQueryWithCrossFilters,
  buildQuery,
} from '../advanced-search/utils/build-query';
import { getDefaultChatContext } from '../ask-anything/ask-anything-button/utils';
import { useDocumentsBulkActionsPanel } from '../search/useDocumentsBulkActionsPanel';
import {
  buildQueryToSave,
  extractFeaturedClauses,
  toDashboardData,
  toTableViewFields,
} from './Dashboard.utils';
import { EntityType } from './types';
import { useCharts } from './useCharts';

export const Dashboard = ({ dashboard }: { dashboard: DashboardType }) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const trackSegment = useTrackSegment();
  const entity = dashboard.entity as EntityType;

  const { data: searchFilters, isFetching: isFetchingFilters } =
    api.endpoints.getFiltersV2.useQuery({
      type: entity,
    });
  const [fetchCurrentTableView, tableViewResult] =
    api.endpoints.getCurrentTableView.useLazyQuery();
  const { data: tableView, isFetching: isFetchingTableView } = tableViewResult;
  const [updateTableView] = api.endpoints.updateTableView.useMutation();

  const {
    getPinnedFilters,
    updatePinnedFilters,
    pinnedFiltersIds,
    isFetching: isFetchingPinnedFilters,
  } = usePinnedFilters(searchFilters, TableViewContextType.EVISEARCH_SEARCH);

  const {
    tableState,
    handleUpdateTableState,
    partialUpdateTableState,
    clearSelectedRows,
  } = useTableState({
    id: `ResultsTable:${dashboard.id}`,
    columnOrder: dashboard.columns,
    sortBy: DEFAULT_SORT_BY[entity],
  });

  const user = useCurrentUser();
  const canSaveDashboard = testIsAdmin(user) || dashboard.creatorId === user.id;

  const activeDashboard = useSelector(
    slices.dashboardV2.selectors.selectActiveDashboard,
  );
  const isCurrentDashboardActive = activeDashboard.id === dashboard.id;
  const activeFilters = useSelector(
    slices.dashboardV2.selectors.selectActiveFilters,
  );
  const queryBuilder = useSelector(
    slices.dashboardV2.selectors.selectQueryBuilder,
  );
  const queryBuilderPanelVisible = useSelector(
    slices.dashboardV2.selectors.selectQueryBuilderPanelVisible,
  );
  const searchText = useSelector(slices.dashboardV2.selectors.selectSearchText);
  const selectedFilters = useSelector(
    slices.dashboardV2.selectors.selectSelectedFilters,
  );
  const hasDataChanges = useSelector(
    slices.dashboardV2.selectors.selectDashboardDirty,
  );
  const isQueryBuilderSupported = useMemo(
    () => testIsQueryBuilderSupported(queryBuilder),
    [queryBuilder],
  );

  const resultsCache = useSelector(
    slices.dashboardV2.selectors.selectResultsCache,
  );

  const {
    charts,
    intervals,
    chartWidths,
    crossFilters,
    currentChart,
    handleRearrangeCharts,
    handleCrossFiltersChange,
    handleSelectCrossFilter,
    onIntervalChange,
    onChartWidthChange,
    onChartTypeChange,
    onRemoveItem,
  } = useCharts();

  const featuredClauses = useMemo(
    () => extractFeaturedClauses(activeFilters),
    [activeFilters],
  );

  const fieldsWithLazyLoad = useMemo(
    () =>
      attachRenderersAndSerializers({
        filters: Object.values(searchFilters || {}),
        type: entity,
      }),
    [searchFilters],
  );

  const [isLoadViewVisible, setIsLoadViewVisible] = useState(false);
  const [isSaveViewVisible, setIsSaveViewVisible] = useState(false);
  const [documentDetails, setDocumentDetails] =
    useState<Nullable<SearchItem>>(null);

  const query = useMemo(() => {
    if (!searchFilters) return [];

    if (queryBuilder) {
      return buildComplexQueryWithCrossFilters(
        queryBuilder,
        crossFilters,
        searchFilters,
        searchText ?? '',
      );
    }

    return buildQuery({
      booleanQuery: searchText ?? '',
      filters: activeFilters,
      crossFilters,
      searchFilters,
    });
  }, [searchText, activeFilters, crossFilters, queryBuilder, searchFilters]);

  const requestFilters = useMemo(
    () => [...STATIC_COLUMNS[entity], ...tableState.columnOrder],
    [entity, tableState.columnOrder],
  );

  const {
    data: searchResponse,
    isFetching: isFetchingSearch,
    refetch: performSearch,
  } = api.endpoints.search.useQuery(
    {
      pageSize: tableState.pageSize,
      page: tableState.pageIndex,
      sortBy: tableState.sortBy,
      type: entity,
      query,
      filters: requestFilters,
    },
    { skip: !isCurrentDashboardActive },
  );

  const {
    panel: bulkActionPanel,
    limitExceededModal,
    getBulkActions,
  } = useDocumentsBulkActionsPanel({
    selectedDocumentIds: tableState?.selectedIds,
    isAllSelected: tableState?.isAllSelected,
    totalCount: searchResponse?.meta?.total ?? 0,
    query,
    searchAPI: 'v3',
    onActionCompleted: () => {
      clearSelectedRows();
      performSearch();
    },
    resultsCache,
  });

  const [handleNavigate] = useSearchNavigation({
    sortBy: tableState.sortBy,
    query,
    pathname: `/dashboards/${dashboard.id}`,
    name: 'dashboard results',
  });

  const {
    DetailsPanel,
    panelProps,
    renderMultipleLocationModal,
    openMultipleLocationsModal,
  } = useDocumentDetails({
    index: searchResponse?.results?.indexOf(documentDetails!) ?? -1,
    count: searchResponse?.results?.length ?? 0,
    handlers: getDocumentHandlers(documentDetails),
    featuredClauses,
    fullTextMatches: documentDetails?.highlights?.full_text_matches,
    onHide: () => setDocumentDetails(null),
    onNavigate: (index) =>
      setDocumentDetails(searchResponse?.results?.[index] ?? null),
    onNavigateToDocument: handleNavigate,
  });

  const dashboardData = useMemo(
    () =>
      toDashboardData({
        dashboard,
        charts,
        columnOrder: tableState.columnOrder,
        selectedFilters,
        intervals,
        chartWidths,
        query,
        entity,
      }),
    [
      dashboard,
      charts,
      tableState.columnOrder,
      selectedFilters,
      intervals,
      chartWidths,
      query,
    ],
  );

  const queryToSave = useMemo(
    () =>
      buildQueryToSave({
        queryBuilder,
        searchFilters,
        activeFilters,
        searchText,
      }),
    [activeFilters, queryBuilder, searchFilters, searchText],
  );

  const handleBooleanSearch = useCallback(
    (value: string) => {
      dispatch(slices.dashboardV2.actions.setSearchText(value));
      partialUpdateTableState({ pageIndex: 1 });
    },
    [dispatch, partialUpdateTableState],
  );

  const handleColumnOrderChange = useCallback(
    (columnOrder: string[]) => {
      partialUpdateTableState({ columnOrder });
      dispatch(slices.dashboardV2.actions.setDashboardDirty(true));
      if (dashboard.is_default) {
        updateTableView({
          context: TableViewContextType.EVISEARCH_SEARCH,
          fields: toTableViewFields(columnOrder),
        });
      }
    },
    [dispatch, partialUpdateTableState, dashboard.is_default, updateTableView],
  );

  const handleFiltersChange = useCallback(
    (filters: Filter[]) => {
      const addedFieldIds = filters
        .filter((f) => !selectedFilters.some((sf) => sf.fieldId === f.fieldId))
        .map((f) => String(f.fieldId));
      if (addedFieldIds.length > 0) {
        trackSegment('selectAddFilters', {
          Name: dashboard.name,
          Value: addedFieldIds.join(','),
        });
      }
      dispatch(slices.dashboardV2.actions.setSelectedFilters(filters));
      partialUpdateTableState({ pageIndex: 1 });
      dispatch(slices.dashboardV2.actions.setDashboardDirty(true));
    },
    [
      selectedFilters,
      trackSegment,
      dashboard.name,
      dispatch,
      partialUpdateTableState,
    ],
  );

  const handleSearchQueryBuilder = useCallback(
    (query: SearchQuery) => {
      dispatch(slices.dashboardV2.actions.setQueryBuilder(query));
      dispatch(slices.dashboardV2.actions.setDashboardDirty(true));
      dispatch(slices.dashboardV2.actions.setQueryBuilderPanelVisible(false));
      dispatch(slices.dashboardV2.actions.setSearchText(''));
      partialUpdateTableState({ pageIndex: 1 });
    },
    [dispatch, partialUpdateTableState],
  );

  const initializeDashboard = useCallback(
    (filters: string[] = []) => {
      if (!searchFilters) return;
      dispatch(
        slices.dashboardV2.actions.initDashboard({
          dashboard: {
            ...dashboard,
            filters: union(dashboard.filters, filters),
          },
          searchFilters,
          tableSettings: { ...tableState, columnWidths: {} },
          defaultSortBy: DEFAULT_SORT_BY[entity],
        }),
      );
    },
    [searchFilters, dispatch, dashboard, tableState, entity],
  );

  const handleOpenQueryBuilder = useCallback(() => {
    dispatch(slices.dashboardV2.actions.setQueryBuilderPanelVisible(true));
    trackSegment('selectQueryBuilder', { Name: dashboard.name });
  }, [dispatch, trackSegment, dashboard.name]);

  const handleResetFilters = useCallback(() => {
    initializeDashboard(pinnedFiltersIds as string[]);
    partialUpdateTableState({ pageIndex: 1 });
    trackSegment('selectClearFilters', { Name: dashboard.name });
  }, [
    initializeDashboard,
    pinnedFiltersIds,
    partialUpdateTableState,
    trackSegment,
    dashboard.name,
  ]);

  useEffect(() => {
    if (!searchFilters) return;

    if (dashboard.is_default && dashboard.name === 'Documents') {
      getPinnedFilters()
        .unwrap()
        .then((res) => initializeDashboard(res?.data));

      fetchCurrentTableView({
        context: TableViewContextType.EVISEARCH_SEARCH,
      });
    } else {
      getPinnedFilters();
      initializeDashboard();
    }

    dispatch(
      slices.chatbot.actions.setContext([
        { ...getDefaultChatContext(intl), primary: true },
      ]),
    );
  }, [searchFilters, dashboard]);

  const isDocumentDashboard = entity === 'document';

  useEffect(() => {
    if (isDocumentDashboard) {
      if (tableView?.length) {
        const tableViewFieldIds = tableView.map(
          (tableViewItem) => tableViewItem.field_id,
        ) as string[];

        partialUpdateTableState({ columnOrder: tableViewFieldIds });
      }
    }
  }, [tableView, isDocumentDashboard]);

  const columnOrderIds = useMemo(
    () => toTableViewFields(tableState.columnOrder),
    [tableState.columnOrder],
  );

  useEffect(() => {
    if (searchResponse?.results) {
      dispatch(
        slices.dashboardV2.actions.patchEvisearchResultsCache(
          searchResponse.results,
        ),
      );
      if (searchResponse.results.length > 0) {
        dispatch(
          slices.chatbot.actions.setContext([
            getDefaultChatContext(intl),
            {
              label: intl.formatMessage({
                id: 'Dashboard.allResultsLabel',
                defaultMessage: 'All Results',
                description: 'Label for all search results context',
              }),
              query,
              title: intl.formatMessage(
                {
                  id: 'Dashboard.allResultsTitle',
                  defaultMessage: 'All Results ({total})',
                  description:
                    'Title showing all results with total count. Example: total = 150',
                },
                { total: searchResponse?.meta?.total },
              ),
              icon: 'search',
              primary: true,
              searchEntity: entity,
              selectedIds: [],
              id: dashboard.id,
              type: 'all_results',
            },
          ]),
        );
      }
    }

    return () => {
      dispatch(
        slices.chatbot.actions.setContext([getDefaultChatContext(intl)]),
      );
    };
  }, [searchResponse, query, entity, intl, dashboard.id]);

  const isLoading =
    isFetchingFilters || isFetchingSearch || isFetchingTableView;

  return (
    <ContentContainer
      loadingContent={{
        isLoading: !isCurrentDashboardActive,
      }}
    >
      <>
        <Layout align="center" justify="space-between" spacing={8}>
          <Text as="h1" m={0} variant="title">
            {capitalize(dashboard.name)}
          </Text>
          <Layout align="center" spacing={4}>
            <EditCharts entity={entity} />
          </Layout>
        </Layout>
        <Layout direction="row" spacing={4} justify="space-between">
          <Box w="100%">
            {isDocumentDashboard && (
              <BooleanSearchBar
                value={searchText ?? ''}
                onSubmit={handleBooleanSearch}
                isSubmitting={isLoading || !!queryBuilder}
              />
            )}
          </Box>

          <SaveDashboard
            dashboardData={dashboardData}
            query={queryToSave}
            isDashboardDirty={hasDataChanges}
            hasSavePermission={canSaveDashboard}
            onSaveSucess={() =>
              dispatch(slices.dashboardV2.actions.setDashboardDirty(false))
            }
          />
        </Layout>

        {queryBuilder ? (
          <>
            <ReadOnlyComplexQuery
              queryBuilder={queryBuilder}
              isQueryBuilderSupported={isQueryBuilderSupported}
              searchFilters={searchFilters || {}}
              onClear={() => {
                dispatch(slices.dashboardV2.actions.setQueryBuilder(undefined));
                dispatch(
                  slices.dashboardV2.actions.setQueryBuilderPanelVisible(false),
                );
              }}
              onEdit={() => {
                dispatch(
                  slices.dashboardV2.actions.setQueryBuilderPanelVisible(true),
                );
              }}
            />

            <Layout align="center" spacing={2} role="group">
              <CrossFilters
                searchFilters={fieldsWithLazyLoad}
                filters={crossFilters}
                onChange={handleCrossFiltersChange}
              />
            </Layout>
          </>
        ) : (
          <Filters
            isLoading={isFetchingFilters || isFetchingPinnedFilters}
            selectedFilters={selectedFilters}
            searchFilters={fieldsWithLazyLoad}
            pinnedFiltersIds={pinnedFiltersIds}
            crossFilters={crossFilters}
            onChange={handleFiltersChange}
            onChangeCrossFilters={handleCrossFiltersChange}
            onQueryButtonClick={handleOpenQueryBuilder}
            onFiltersReset={handleResetFilters}
            onUpdatePinnedFilters={(fieldIds) => updatePinnedFilters(fieldIds)}
          />
        )}
        <Charts
          charts={charts}
          entity={entity}
          intervals={intervals}
          chartWidths={chartWidths}
          onCrossFiltersChange={handleSelectCrossFilter}
          onIntervalChange={onIntervalChange}
          onChartWidthChange={onChartWidthChange}
          onChartTypeChange={onChartTypeChange}
          onRearrangeCharts={handleRearrangeCharts}
          onRemoveItem={onRemoveItem}
          queryParams={{
            booleanQuery: searchText,
            filters: activeFilters,
            crossFilters,
            searchFilters,
            queryBuilder,
          }}
          searchParams={{
            page: tableState.pageIndex,
            pageSize: tableState.pageSize,
            sortBy: tableState.sortBy,
            type: entity,
            query,
            filters: requestFilters,
          }}
          currentChart={currentChart}
        />
        <ResultsTable
          data={searchResponse}
          id={dashboard.id}
          isLoading={isLoading}
          enablePersistedTable={dashboard.is_default}
          entity={entity}
          query={query}
          searchFilters={searchFilters ?? {}}
          state={{
            columnOrder: tableState.columnOrder,
            pageIndex: tableState.pageIndex,
            pageSize: tableState.pageSize,
            sortBy: [tableState.sortBy],
            selectedRowIds: tableState.selectedIds,
          }}
          getBulkActions={isDocumentDashboard ? getBulkActions : undefined}
          onUpdateState={handleUpdateTableState}
          onSetColumnOrder={handleColumnOrderChange}
          onNavigate={isDocumentDashboard ? handleNavigate : undefined}
          onDocumentNameClick={
            isDocumentDashboard
              ? (row) =>
                  openMultipleLocationsModal(getDocumentHandlers(row), false)
              : undefined
          }
          onRowDetailsClick={
            isDocumentDashboard ? setDocumentDetails : undefined
          }
          onSaveView={
            isDocumentDashboard ? () => setIsSaveViewVisible(true) : undefined
          }
          onLoadView={
            isDocumentDashboard ? () => setIsLoadViewVisible(true) : undefined
          }
        />
        {DetailsPanel && <DetailsPanel {...panelProps} />}
        {renderMultipleLocationModal?.()}
        {queryBuilderPanelVisible && (
          <QueryBuilder
            query={queryBuilder}
            pinnedFiltersIds={pinnedFiltersIds}
            searchFilters={fieldsWithLazyLoad}
            onQueryChange={handleSearchQueryBuilder}
            onHide={() =>
              dispatch(
                slices.dashboardV2.actions.setQueryBuilderPanelVisible(false),
              )
            }
          />
        )}

        {limitExceededModal}
        {bulkActionPanel}

        {isSaveViewVisible && (
          <BaseSaveView
            context={TableViewContextType.EVISEARCH_SEARCH}
            fields={columnOrderIds}
            hideModal={() => setIsSaveViewVisible(false)}
            modalType={MODAL_DOCUMENTS_COLUMN_FIELDS_SAVE}
          />
        )}

        {isLoadViewVisible && (
          <BaseLoadView
            context={TableViewContextType.EVISEARCH_SEARCH}
            modalType={MODAL_DOCUMENTS_COLUMN_FIELDS_VIEW_SWITCH}
            hideModal={() => setIsLoadViewVisible(false)}
            onViewSelected={() =>
              fetchCurrentTableView({
                context: TableViewContextType.EVISEARCH_SEARCH,
              })
            }
          />
        )}
      </>
    </ContentContainer>
  );
};
